import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {BaseComponent} from '../../core/base.component';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import {AuthService} from '../../core/security/auth.service';
import {RolesEnum} from '../../core/security/roles.enum';
import {FirewallService} from '../firewall.service';
import {FirewallModel} from '../../api/models/firewall-model';
import {maxDateRange} from '../../core/validators/max-date-range.validator';
import {ipAddressValidator} from '../../core/validators/ip-address.validator';
import {DateTime} from 'luxon';
import {LookupService} from '../../api/services/lookup.service';
import {LookupModel} from '../../api/models/lookup-model';
import {requiredBy} from '../../core/validators/required-by.validator';
import {MatDialog} from '@angular/material/dialog';
import {MatConfirmDialogComponent} from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatCard, MatCardContent, MatCardActions } from '@angular/material/card';
import { NgIf } from '@angular/common';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatMultiselectComponent } from '../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatTextareaInputComponent } from '../../editors/mat-textarea-input/mat-textarea-input.component';
import { FlexModule } from '@angular/flex-layout';
import { MatDateInputComponent } from '../../editors/mat-date-input/mat-date-input.component';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-firewall-edit',
    templateUrl: './firewall-edit.component.html',
    styleUrls: ['./firewall-edit.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        NgIf,
        MatTextInputComponent,
        MatMultiselectComponent,
        MatTextareaInputComponent,
        FlexModule,
        MatDateInputComponent,
        MatCheckboxInputComponent,
        MatCardActions,
        MatButton,
        MatTooltip,
        Angular2PromiseButtonModule,
        MatIcon,
    ],
})
export class FirewallEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  form: UntypedFormGroup;
  ipAddressControl = new UntypedFormControl('', [ipAddressValidator, Validators.required]);
  customerNoteControl = new UntypedFormControl([], []);
  additionalNotesControl = new UntypedFormControl([], []);
  startOnControl = new UntypedFormControl('', [Validators.required]);
  endOnControl = new UntypedFormControl('', [Validators.required]);
  notifyExpirationControl = new UntypedFormControl(true, []);
  recipientsControl = new UntypedFormControl([], []);

  submitted: boolean;
  serverError: boolean;
  lastServerError: string;
  availablePbxAccounts: LookupModel[] = [];

  editModel: FirewallModel = {
    id: 'new',
    ipAddress: '',
    groupName: 'AllowIP',
    startOn: new Date().toDateString(),
    endOn: DateTime.now().plus({day: 90}).toJSDate().toDateString(),
    notifyExpiration: true,
    recipients: []
  };
  clientIpAddress = 'unknown';
  firewallLimits = 2;
  buttonSubmitPromise: Promise<void>;
  private paramsSubscription;
  private id: string;

  constructor(route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService,
              public dialog: MatDialog,
              private firewallService: FirewallService,
              private lookupService: LookupService,
              private fb: UntypedFormBuilder,
              private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.paramsSubscription = this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });

    this.editRoles = [RolesEnum.Firewall_Edit, RolesEnum.Firewall_Delete];
    this.deleteRoles = [RolesEnum.Firewall_Delete];

    this.headerService.showBackButton(true);

    this.createForm();
  }

  createForm(): void {
    if (this.firewallLimits <= 0) {
      this.ipAddressControl = new UntypedFormControl('', [ipAddressValidator]);
      this.customerNoteControl = new UntypedFormControl([], []);
      this.additionalNotesControl = new UntypedFormControl([], []);
      this.startOnControl = new UntypedFormControl('');
      this.endOnControl = new UntypedFormControl('');
      this.notifyExpirationControl = new UntypedFormControl(false, []);
      this.recipientsControl = new UntypedFormControl([], [requiredBy('notifyExpiration')]);

      this.form = this.fb.group({
          'ipAddress': this.ipAddressControl,
          'customerNote': this.customerNoteControl,
          'additionalNotes': this.additionalNotesControl,
          'startOn': this.startOnControl,
          'endOn': this.endOnControl,
          'notifyExpiration': this.notifyExpirationControl,
          'recipients': this.recipientsControl,
        },
        {validators: [maxDateRange('startOn', 'endOn', 1, 'years')]}
      );
    } else {
      const currentUser = this.authService.currentUser.getValue();
      this.ipAddressControl = new UntypedFormControl('', [ipAddressValidator, Validators.required]);
      this.additionalNotesControl = new UntypedFormControl([], []);
      this.startOnControl = new UntypedFormControl(undefined, [Validators.required]);
      this.endOnControl = new UntypedFormControl(undefined, [Validators.required]);
      this.notifyExpirationControl = new UntypedFormControl(true, []);
      this.recipientsControl = new UntypedFormControl([], [requiredBy('notifyExpiration')]);

      if (currentUser.domain && this.availablePbxAccounts.length > 0) {
        this.customerNoteControl = new UntypedFormControl([], [Validators.required]);
      } else {
        this.customerNoteControl = new UntypedFormControl([], []);
      }

      this.form = this.fb.group({
          'ipAddress': this.ipAddressControl,
          'customerNote': this.customerNoteControl,
          'additionalNotes': this.additionalNotesControl,
          'startOn': this.startOnControl,
          'endOn': this.endOnControl,
          'notifyExpiration': this.notifyExpirationControl,
          'recipients': this.recipientsControl,
        },
        {
          validators: [maxDateRange('startOn', 'endOn', 1, 'years')]
        }
      );
    }
  }

  async onSubmit() {
    this.serverError = false;
    this.submitted = true;

    if (this.firewallLimits <= 0) {
      this.buttonSubmitPromise = this.processRequestSubmit();
    } else {
      if (this.form.valid && this.hasEditAccess) {
        const reqData = this.prepareFormModel();
        if (reqData.notifyExpiration && reqData.recipients.length === 0) {
          this.submitted = false;
          this.notificationService.notifyDanger('Please enter email addresses to notify about expiration');
        } else {
          this.buttonSubmitPromise = this.processSubmit();
        }
      } else {
        setTimeout(() => {
          this.submitted = false;
        });
      }
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.firewallService.saveIpAddress(reqData);
      this.notificationService.notifySuccess('Request successfully submitted');
      this.headerService.goBack();
    } catch (e: any) {
      if (e.error?.message) {
        this.lastServerError = e.error.message;
      } else {
        this.lastServerError = '';
      }
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  async processRequestSubmit(): Promise<void> {
    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.firewallService.requestExtendLimits(reqData);
      this.notificationService.notifySuccess('Request successfully submitted');
      this.headerService.goBack();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): FirewallModel {
    const formModel = this.form.getRawValue();

    return {
      id: this.editModel.id,
      groupName: this.editModel.groupName,
      ipAddress: formModel.ipAddress,
      customerNote: formModel.customerNote.join(', '),
      additionalNotes: formModel.additionalNotes,
      startOn: formModel.startOn,
      endOn: formModel.endOn,
      notifyExpiration: formModel.notifyExpiration || false,
      recipients: formModel.recipients
    };
  }

  async ngOnInit() {
    super.ngOnInit();

    if (this.id !== 'new') {
      this.editModel = await this.firewallService.getFirewallIpAddressById(this.id);
    } else {
      // check if user can create new IP address
      this.firewallLimits = await this.firewallService.getFirewallLimits();
    }

    this.lookupService.lookupGetCustomerExtensions({types: ['extensions', 'acds', 'hunts']}).toPromise().then((values) => {
      this.availablePbxAccounts = values.data;

      this.createForm();
      this.ngOnChanges();

      this.firewallService.getClientIpAddress()
        .then(value => this.clientIpAddress = value);
    });
  }

  ngOnChanges(changes?: SimpleChanges): void {
    this.form.reset({
      ipAddress: this.editModel.ipAddress,
      customerNote: this.editModel.customerNote ? this.editModel.customerNote.split(',').map(x => x.trim()) : [],
      additionalNotes: this.editModel.additionalNotes,
      startOn: this.editModel.startOn ? new Date(this.editModel.startOn) : null,
      endOn: this.editModel.endOn ? new Date(this.editModel.endOn) : null,
      notifyExpiration: this.editModel.notifyExpiration,
      recipients: this.editModel.recipients
    });
  }

  copyIpAddress() {
    this.form.patchValue({
      ipAddress: this.clientIpAddress
    });
  }

  onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete IP Address?',
        text: 'Are you sure you want to delete IP address from whitelist?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.firewallService.deleteIpAddress(this.editModel.id);
        await this.notificationService.notifySuccess('IP address has been deleted');

        this.headerService.goBack();
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  onStartDateChange($event: any) {
    console.log($event);
    this.form.patchValue({
      endOn: DateTime.fromJSDate($event).plus({day: 90}).toJSDate(),
    });
  }

  notifyExpirationChanged($event: boolean) {
    console.log($event);
    this.recipientsControl.markAsTouched({onlySelf: true});
    this.recipientsControl.markAsDirty({onlySelf: true});
    this.recipientsControl.updateValueAndValidity();
  }
}
